import type {
  BasicData,
  Child,
  Field,
  FieldConstraints,
  Group,
  Rule,
  RuleReference,
  RuleVersionReference,
  Schema,
  SchemaDocument,
  VersionChild,
} from "../project";
import type { xdf3 } from "../xdatenfelder";

export function createSchemaDocument(
  message: xdf3.SchemaMessage,
): SchemaDocument {
  const schema = convertSchema(message.schema);
  if (schema.type !== "version") {
    throw new Error("Expected versioned schema");
  }

  const groups: Record<string, Group> = {};
  for (const xdfGroup of Object.values(message.gruppen)) {
    const group = convertGroup(xdfGroup);
    groups[group.fimId] = group;
  }

  const fields: Record<string, Field> = {};
  for (const xdfField of Object.values(message.felder)) {
    const field = convertField(xdfField);
    fields[field.fimId] = field;
  }

  const rules: Record<string, Rule> = {};
  for (const xdfRule of Object.values(message.regeln)) {
    const rule = convertRule(xdfRule);
    rules[rule.fimId] = rule;
  }

  return {
    type: "schema",
    schema,
    groups,
    fields,
    rules,
  };
}

function convertSchema(schema: xdf3.Schema): Schema {
  if (schema.version === undefined) {
    return {
      type: "arbeitskopie",
      fimId: schema.id,
      bezeichnung: schema.label,
      hilfeText: schema.help,
      ableitungsModifikationenRepraesentation:
        schema.ableitungsmodifikationenRepraesentation,
      ableitungsModifikationenStrutkur: schema.ableitungsmodifikationenStruktur,
      documentSteckbriefFimId: schema.dokumentsteckbrief.id,
      basicData: convertAllgemeineAngaben(schema),
      children: convertChildren(schema.children),
      rules: convertRuleReferences(schema.rules),
    };
  } else {
    return {
      type: "version",
      url: `https://fimportal.de/schemas/${schema.id}/${schema.version}`,
      fimId: schema.id,
      fimVersion: schema.version,
      bezeichnung: schema.label,
      hilfeText: schema.help,
      ableitungsModifikationenRepraesentation:
        schema.ableitungsmodifikationenRepraesentation,
      ableitungsModifikationenStrutkur: schema.ableitungsmodifikationenStruktur,
      documentSteckbriefFimId: schema.dokumentsteckbrief.id,
      basicData: convertAllgemeineAngaben(schema),
      children: convertVersionChildren(schema.children),
      rules: convertRuleVersionReferences(schema.rules),
    };
  }
}
function convertRule(rule: xdf3.Rule): Rule {
  if (rule.version === undefined) {
    return {
      type: "arbeitskopie",
      fimId: rule.id,
      name: rule.name,
      beschreibung: rule.description,
      freitextRegel: rule.freeFormDefinition,
      bezug: rule.normReferences,
      stichwort: rule.keywords,
      fachlicherErsteller: rule.creator,
      letzteAenderung: rule.lastChangedAt,
      ruleType: "valid",
      skript: rule.script,
      expression: undefined,
      target: undefined,
    };
  } else {
    return {
      type: "version",
      fimId: rule.id,
      fimVersion: rule.version,
      name: rule.name,
      beschreibung: rule.description,
      freitextRegel: rule.freeFormDefinition,
      bezug: rule.normReferences,
      stichwort: rule.keywords,
      fachlicherErsteller: rule.creator,
      letzteAenderung: rule.lastChangedAt,
      ruleType: "valid",
      skript: rule.script,
    };
  }
}

function convertGroup(group: xdf3.DataGroup): Group {
  if (group.version === undefined) {
    return {
      type: "arbeitskopie",
      fimId: group.id,
      basicData: convertAllgemeineAngaben(group),
      art: group.art,
      children: convertChildren(group.children),
      rules: convertRuleReferences(group.rules),
    };
  } else {
    return {
      type: "version",
      url: `https://fimportal.de/groups/baukasten/${group.id}/${group.version}`,
      namespace: "baukasten",
      fimId: group.id,
      fimVersion: group.version,
      basicData: convertAllgemeineAngaben(group),
      art: group.art,
      children: convertVersionChildren(group.children),
      rules: convertRuleVersionReferences(group.rules),
    };
  }
}

function convertField(field: xdf3.DataField): Field {
  if (field.version === undefined) {
    return {
      type: "arbeitskopie",
      fimId: field.id,
      basicData: convertAllgemeineAngaben(field),
      inputType: field.inputType,
      dataType: field.dataType,
      value: field.content,
      rules: convertRuleReferences(field.rules),
      options: [],
      constraints: convertPraezisierung(),
    };
  } else {
    return {
      type: "version",
      url: `https://fimportal.de/fields/baukasten/${field.id}/${field.version}`,
      namespace: "baukausten",
      fimId: field.id,
      fimVersion: field.version,
      basicData: convertAllgemeineAngaben(field),
      inputType: field.inputType,
      dataType: field.dataType,
      value: field.content,
      rules: convertRuleVersionReferences(field.rules),
      options: [],
      constraints: convertPraezisierung(),
    };
  }
}

function convertPraezisierung(): FieldConstraints {
  // TODO: implement
  return {
    minValue: undefined,
    maxValue: undefined,
    minLength: undefined,
    maxLength: undefined,
    pattern: undefined,
  };
}

function convertAllgemeineAngaben(data: xdf3.BaseData): BasicData {
  return {
    name: data.name,
    beschreibung: data.description,
    definition: data.definition,
    normReferences: data.normReferences,
    releaseStatus: data.releaseState,
    statusGesetztAm: data.stateSetAt,
    statusGesetztDurch: data.stateSetBy,
    gueltigAb: data.validSince,
    gueltigBis: data.validUntil,
    versionshinweis: data.versionHint,
    veroeffentlichungsdatum: data.publishedAt,
    relations: data.relations,
    keywords: data.keywords,
  };
}

function convertChildren(children: xdf3.ChildRef[]): Child[] {
  return children.map((ref) => {
    return {
      type: getChildType(ref.type),
      element: {
        fimId: ref.element.id,
        fimVersion: ref.element.version,
      },
      cardinality: ref.anzahl,
    };
  });
}

function getChildType(childType: "dataField" | "dataGroup"): "field" | "group" {
  switch (childType) {
    case "dataField":
      return "field";
    case "dataGroup":
      return "group";
  }
}

function convertVersionChildren(children: xdf3.ChildRef[]): VersionChild[] {
  return children.map((ref) => {
    if (ref.element.version === undefined) {
      throw new Error(`Expected versioned child reference: ${ref.element}`);
    }

    return {
      type: getChildType(ref.type),
      element: {
        fimId: ref.element.id,
        fimVersion: ref.element.version,
      },
      cardinality: ref.anzahl,
    };
  });
}

function convertRuleReferences(
  references: xdf3.ElementIdentifikation[],
): RuleReference[] {
  return references.map((ref) => {
    return { fimId: ref.id, fimVersion: ref.version };
  });
}

function convertRuleVersionReferences(
  references: xdf3.ElementIdentifikation[],
): RuleVersionReference[] {
  return references.map((ref) => {
    if (ref.version === undefined) {
      throw new Error(`Expected versioned rule reference: ${ref}`);
    }

    return { fimId: ref.id, fimVersion: ref.version };
  });
}
